import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from './api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import ProductNav from './components/ProductNav';
import './VeridianPurchasePage.css';

const VERIDIAN_PRICE = 'US$14.99';
const DUAL_PRICE = 'US$24.95';

function PurchaseButton({ children, label, available, busy, onClick }) {
  const disabled = !available || busy;
  return (
    <button
      type="button"
      className="vd-purchase__button"
      disabled={disabled}
      aria-label={available ? label : `${label}, checkout unavailable`}
      title={available ? undefined : 'Purchasing is not available yet'}
      onClick={onClick}
    >
      {busy ? 'Opening checkout...' : children}
    </button>
  );
}

export default function VeridianPurchasePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const moreInfoRoute = location.pathname.startsWith('/preview/')
    ? '/preview/rpg/veridian/more-info'
    : '/dashboard/rpg/veridian/more-info';
  const consentCheckout = useGuardedCheckout();
  const [purchasingAvailable, setPurchasingAvailable] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);
  const [purchasingPlan, setPurchasingPlan] = useState(null);
  const [pageError, setPageError] = useState('');

  useEffect(() => {
    let cancelled = false;
    API.get('/v1/rpg/account')
      .then((response) => {
        if (!cancelled) {
          setPurchasingAvailable(response.data?.purchasing?.available === true);
          setAvailabilityChecked(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPurchasingAvailable(false);
          setAvailabilityChecked(true);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const continueToStripe = (response) => {
    const checkoutUrl = response?.data?.checkout_url;
    if (checkoutUrl) {
      window.location.assign(checkoutUrl);
      return true;
    }
    setPageError('Could not start checkout. Please try again.');
    return false;
  };

  const startCheckout = async (planKey) => {
    setPurchasingPlan(planKey);
    setPageError('');
    try {
      const response = await consentCheckout.start(
        () => API.post('/v1/rpg/checkout', { plan_key: planKey }),
        { purchaseType: 'rpg_worlds', intendedSelector: planKey },
      );
      if (response) continueToStripe(response);
    } finally {
      setPurchasingPlan(null);
    }
  };

  const purchasingBusy = purchasingPlan !== null || consentCheckout.busy;
  const checkoutError = pageError || consentCheckout.error;

  return (
    <main className="vd-purchase">
      <ProductNav />

      <section
        className="vd-purchase__hero"
        aria-labelledby="veridian-title"
        style={{ backgroundImage: "url('/gaming/veridian-world.png')" }}
      >
        <div className="vd-purchase__shell">
          <div className="vd-purchase__hero-content">
            <div className="vd-purchase__eyebrow">Persistent Fantasy RPG</div>
            <h1 id="veridian-title">VERIDIAN</h1>
            <p className="vd-purchase__subtitle">One human. Up to four persistent characters. One living world.</p>
            <p className="vd-purchase__intro">
              Long-term fantasy role-playing driven by real game systems with AI narration. Your party, discoveries, relationships, ownership and consequences carry forward across sustained play.
            </p>
            <div className="vd-purchase__price-line">
              <strong>{VERIDIAN_PRICE}</strong><span>/ month</span>
            </div>
            <div className="vd-purchase__actions">
              <PurchaseButton
                label="Subscribe to Veridian"
                available={purchasingAvailable}
                busy={purchasingBusy}
                onClick={() => startCheckout('veridian')}
              >Subscribe to Veridian</PurchaseButton>
              <button type="button" className="vd-purchase__button vd-purchase__button--secondary" onClick={() => navigate(moreInfoRoute)}>
                More Info
              </button>
              {!location.pathname.startsWith('/preview/') && <button type="button" className="vd-purchase__button vd-purchase__button--secondary"
                onClick={() => navigate('/dashboard/rpg/veridian/characters')}>Create characters</button>}
            </div>
          </div>
        </div>
      </section>

      <section className="vd-purchase__body">
        <div className="vd-purchase__shell">
          <p className="vd-purchase__principle">THE GAME DETERMINES WHAT HAPPENED. THE NARRATOR TELLS YOU WHAT HAPPENED.</p>

          {!purchasingAvailable ? (
            <div className="vd-purchase__status" role="status">
              <span className="vd-purchase__status-dot" />
              <div>
                <strong>{availabilityChecked ? 'Purchasing is not available yet' : 'Checking purchasing availability'}</strong>
                <p>{availabilityChecked ? 'Veridian access will open here when account purchasing is available.' : 'Confirming secure subscription checkout.'}</p>
              </div>
            </div>
          ) : null}

          {checkoutError ? (
            <div className="vd-purchase__status" role="alert">
              <span className="vd-purchase__status-dot" />
              <div><strong>Checkout could not be opened</strong><p>{checkoutError}</p></div>
            </div>
          ) : null}

          <GuardedCheckoutConsent checkout={consentCheckout} onDone={continueToStripe} />

          <h2 className="vd-purchase__heading">Choose your access</h2>
          <p className="vd-purchase__lead">Choose Veridian on its own, or include both persistent RPG worlds under one combined subscription.</p>

          <div className="vd-purchase__plans">
            <article className="vd-purchase__plan">
              <div className="vd-purchase__plan-label">One world</div>
              <h3>Veridian</h3>
              <div className="vd-purchase__plan-price"><strong>{VERIDIAN_PRICE}</strong><span>/ month</span></div>
              <p>One persistent fantasy world for one human player and a continuing party of up to four characters.</p>
              <PurchaseButton
                label="Choose Veridian"
                available={purchasingAvailable}
                busy={purchasingBusy}
                onClick={() => startCheckout('veridian')}
              >Choose Veridian</PurchaseButton>
            </article>

            <article className="vd-purchase__plan vd-purchase__plan--dual">
              <div className="vd-purchase__plan-label">Both worlds</div>
              <h3>Veridian + CyberTech</h3>
              <div className="vd-purchase__plan-price"><strong>{DUAL_PRICE}</strong><span>/ month</span></div>
              <p>Both persistent RPG worlds included under one combined monthly subscription.</p>
              <PurchaseButton
                label="Choose Both Worlds"
                available={purchasingAvailable}
                busy={purchasingBusy}
                onClick={() => startCheckout('dual')}
              >Choose Both Worlds</PurchaseButton>
            </article>
          </div>

          <p className="vd-purchase__note">
            {purchasingAvailable
              ? 'Checkout opens securely in Stripe TEST mode. No live payment is collected.'
              : 'No payment is collected from this page while purchasing is unavailable.'}
          </p>
        </div>
      </section>
    </main>
  );
}
